import { cn } from "@d12labs/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

// Linear button spec: 8px radius (rounded-md), 14px font / 500 weight,
// compact 8/14 padding, hover lightens the lavender, focus ring uses the
// design's primary-focus token.
const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-body font-medium leading-none transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring/50 aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
	{
		variants: {
			variant: {
				// Lavender CTA — the signature Linear accent.
				default: "bg-primary text-primary-foreground hover:bg-primary-hover",
				destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
				// Standard shadcn outline — transparent with a border. Spiritually
				// equivalent to the vanilla shadcn variant so `npx shadcn add button`
				// can refresh it without conflicting with our additions.
				outline: "border border-hairline bg-transparent text-foreground hover:bg-accent",
				// Charcoal button — most secondary actions.
				secondary: "bg-secondary text-secondary-foreground border border-hairline hover:bg-accent",
				// Plain text on canvas — for tertiary actions and nav.
				ghost: "bg-transparent text-ink hover:bg-accent",
				link: "text-primary underline-offset-4 hover:underline",
				// ──────────────────────────────────────────────────────────────
				// Additive Linear-specific variants below. These are NOT in
				// upstream shadcn, so a reinstall won't touch them. Keep new
				// design-driven variants here rather than overwriting standards.
				// ──────────────────────────────────────────────────────────────
				// White-on-dark CTA, rare; reserved for inverse section openers.
				inverse:
					"bg-inverse-canvas text-inverse-ink hover:bg-inverse-surface-1 border border-hairline",
			},
			size: {
				// Linear's compact button spec.
				default: "h-9 px-3.5 has-[>svg]:px-3",
				sm: "h-7 px-3 text-caption",
				lg: "h-10 px-5",
				icon: "size-9",
				"icon-sm": "size-7",
				"icon-lg": "size-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	);
}

export { Button, buttonVariants };
